export { FeatureInfoHelper };

class FeatureInfoHelper {

    /**
     * Requests the "getFeatureInfo" method defined by url and responses an
     * array of objects containing the original data and popup styled content.
     *
     * @private
     * @param {string} url
     */
    requestFeatureInfo_(baseUrl, params, upperCase = true) {
        if (this.request_) this.request_.abort();
        this.request_ = new XMLHttpRequest();

        baseUrl = baseUrl + "?";

        const url = baseUrl + L.Util.getParamString(params, baseUrl, upperCase);
        console.log("The URL queried is: ", url);

        return new Promise(resolve => {
            const xhttp = this.request_;
            xhttp.onreadystatechange = () => {
                if (xhttp.readyState == 4) {
                    let response = null;
                    if (xhttp.status == 200) {
                        const type = xhttp.getResponseHeader('content-type').toLowerCase();
                        try {
                            if (type.includes('json')) {
                                response = JSON.parse(xhttp.responseText);
                            } else if (type.includes('xml')) {
                                response = new DOMParser().parseFromString(xhttp.responseText, "text/xml")
                            } else {
                                response = xhttp.responseText;
                            }
                        } catch (e) {
                            console.warn("Could not parse " + type + " response!", e);
                        }
                    } else {
                        console.warn("HTTP status code is " + xhttp.status);
                    }
                    this.request_ = null;
                    resolve(response);
                }
            };

            xhttp.open("GET", url, true);
            xhttp.send();
        });
    }

    createGetFeatureInfoParams_(layer, options, version) {
        version = version || layer.serviceVersion;

        const layers = layer.getFeatureInfoName || layer.getRequestName();
        const min = options.min;
        const max = options.max;

        const params = {
            service: "WMS",
            request: "GetFeatureInfo",
            feature_count: 50,
            buffer: 25,
            layers: layers,
            query_layers: layers,
            styles: layer.getStyle(),
            width: options.container.width,
            height: options.container.height,
            version: version
        };

        // version specific adjustments
        params.bbox = ((version === "1.3.0") && (options.crs == "EPSG:4326"))
            ? [min.y, min.x, max.y, max.x].join(",")
            : [min.x, min.y, max.x, max.y].join(",")
            ;
        params[version === "1.3.0" ? "I" : "X"] = options.container.x;
        params[version === "1.3.0" ? "J" : "Y"] = options.container.y;
        params[version === "1.3.0" ? "CRS" : "SRS"] = options.crs;

        const layerParams = layer.getQueryParams_();
        // Add additional Params
        ["filter", "cql_filter", "time", "env", "query", "layerDefs"].forEach(param => {
            if (param in layerParams) {
                params[param] = layerParams[param];
            }
        });

        return params;
    }

    geoserver(layer, options) {
        return new Promise(resolve => {
            const params = this.createGetFeatureInfoParams_(layer, options);
            params.info_format = "application/json";

            // sort results by the correct time column
            let timeColumn = null;
            for (let i = 0; i < layer.timeColumns_.length; ++i) {
                const column = layer.timeColumns_[i];
                if ((column in layer.dimensions) && (layer.dimensions[column].type == "attribute_field")) {
                    timeColumn = layer.dimensions[column].name || column;
                } else if (column in layer.attributeFields) {
                    timeColumn = column;
                }
                if (timeColumn) {
                    params.sortBy = timeColumn + " A";
                    break;
                }
            }

            this.requestFeatureInfo_(layer.serviceUrl, params)
                .then(response => resolve({ layer: layer, data: response }));
        });
    }

    thredds(layer, options) {
        return new Promise(resolve => {
            const params = this.createGetFeatureInfoParams_(layer, options);
            params.info_format = "text/xml";

            this.requestFeatureInfo_(layer.serviceUrl, params)
                .then(response => {
                    if (!(response instanceof XMLDocument)) {
                        return resolve({ layer: layer, data: response });
                    }
                    const featureCollection = {
                        type: "FeatureCollection",
                        features: []
                    };
                    const lng = parseFloat(response.querySelector("longitude").textContent.trim());
                    const lat = parseFloat(response.querySelector("latitude").textContent.trim());
                    response.querySelectorAll("FeatureInfoResponse FeatureInfo").forEach(xmlFeature => {
                        const feature = {
                            type: "Feature",
                            geometry: {
                                type: "Point",
                                coordinates: [lng, lat]
                            },
                            properties: {}
                        };
                        xmlFeature.childNodes.forEach(child => {
                            if (child.nodeType === 1) {
                                feature.properties[child.tagName.trim()] = child.textContent.trim()
                            }
                        });
                        featureCollection.features.push(feature)
                    });
                    resolve({ layer: layer, data: featureCollection });
                });
        });
    }

    esri(layer, options) {
        return new Promise(resolve => {
            const params = this.createGetFeatureInfoParams_(layer, options, "1.1.1");
            params.info_format = "text/xml";

            this.requestFeatureInfo_(layer.serviceUrl, params)
                .then(response => {
                    if (!(response instanceof XMLDocument)) {
                        return resolve({ layer: layer, data: response });
                    }
                    const featureCollection = {
                        type: "FeatureCollection",
                        features: []
                    };
                    response.querySelectorAll("FeatureInfoResponse FIELDS").forEach(xmlFeature => {
                        const feature = {
                            type: "Feature",
                            properties: {}
                        };
                        for (let i = 0; i < xmlFeature.attributes.length; ++i) {
                            const attribute = xmlFeature.attributes[i];
                            feature.properties[attribute.name] = attribute.value;
                        }
                        featureCollection.features.push(feature)
                    });
                    resolve({ layer: layer, data: featureCollection });
                });
        });
    }

    esri_identify(layer, options) {
        return new Promise(resolve => {
            const layers = layer.getFeatureInfoName || layer.getRequestName();
            const min = options.min;
            const max = options.max;

            const params = {
                geometryPrecision: 1,
                returnZ: "false",
                returnM: "false",
                returnGeometry: "false",
                tolerance: 25,
                f: "json",
                geometryType: "esriGeometryPoint",
                mapExtent: [min.x, min.y, max.x, max.y].join(","),
                geometry: [options.click.x, options.click.y].join(","),
                imageDisplay: options.container.width + "," + options.container.height + ",96",
                layers: "all:" + layers,
                sr: options.crs
            };
            // only add filter and time dimension if defined
            const currentParams = layer.getQueryParams_();
            if (currentParams.layerDefs) params.layerDefs = currentParams.layerDefs;
            if (currentParams.time) params.time = currentParams.time;

            this.requestFeatureInfo_(layer.serviceUrl + "/identify", params, false)
                .then(response => resolve({ layer: layer, data: response }));
        });
    }
}